import { CreateLineAtomicRequestSchema } from "@opus-dt/shared-types";
import type { DraftLine } from "./useLineDraftState";

export interface DraftEntityRef {
  kind:
    | "header"
    | "machine"
    | "parameter"
    | "sensorLink"
    | "compatibility"
    | "node"
    | "edge"
    | "canvas";
  tmpId?: string;
  field?: string;
}

export interface DraftValidationError {
  code: string;
  message: string;
  entityRef: DraftEntityRef;
}

export interface DraftValidationResult {
  ok: boolean;
  errors: DraftValidationError[];
  machinesWithErrors: Set<string>;
}

export function validateDraft(draft: DraftLine): DraftValidationResult {
  const machinesArr = Object.values(draft.machines);
  const payload = {
    header: draft.header,
    machines: machinesArr,
    canvas: draft.canvas,
  };

  const parsed = CreateLineAtomicRequestSchema.safeParse(payload);
  if (parsed.success) {
    return { ok: true, errors: [], machinesWithErrors: new Set() };
  }

  const errors: DraftValidationError[] = [];
  const machinesWithErrors = new Set<string>();

  for (const issue of parsed.error.issues) {
    const path = issue.path;
    let ref: DraftEntityRef = { kind: "header" };

    if (path[0] === "header") {
      ref = { kind: "header", field: String(path[1] ?? "") };
    } else if (path[0] === "machines") {
      const machineIdx = path[1];
      const machine = typeof machineIdx === "number" ? machinesArr[machineIdx] : undefined;
      if (machine) machinesWithErrors.add(machine.tmpId);
      if (path[2] === "parameters") {
        const paramIdx = path[3];
        const param = typeof paramIdx === "number" ? machine?.parameters[paramIdx] : undefined;
        ref = { kind: "parameter", tmpId: param?.tmpId, field: String(path[4] ?? "") };
      } else if (path[2] === "sensorLinks") {
        const slIdx = path[3];
        const sl = typeof slIdx === "number" ? machine?.sensorLinks[slIdx] : undefined;
        ref = { kind: "sensorLink", tmpId: sl?.tmpId, field: String(path[4] ?? "") };
      } else if (path[2] === "compatibilities") {
        const cIdx = path[3];
        const c = typeof cIdx === "number" ? machine?.compatibilities[cIdx] : undefined;
        ref = { kind: "compatibility", tmpId: c?.tmpId, field: String(path[4] ?? "") };
      } else {
        ref = { kind: "machine", tmpId: machine?.tmpId, field: String(path[2] ?? "") };
      }
    } else if (path[0] === "canvas") {
      if (path[1] === "nodes") {
        const node = typeof path[2] === "number" ? draft.canvas.nodes[path[2]] : undefined;
        ref = { kind: "node", tmpId: node?.tmpId };
      } else if (path[1] === "edges") {
        const edge = typeof path[2] === "number" ? draft.canvas.edges[path[2]] : undefined;
        ref = { kind: "edge", tmpId: edge?.tmpId };
      } else {
        ref = { kind: "canvas" };
      }
    }

    errors.push({
      code: issue.code,
      message: issue.message,
      entityRef: ref,
    });
  }

  return { ok: false, errors, machinesWithErrors };
}
